import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { GalleryCard, Gallery } from "./GalleryCard";

interface PortfolioGridProps {
  galleries: Gallery[];
  title?: string;
  subtitle?: string;
}

export const PortfolioGrid: React.FC<PortfolioGridProps> = ({
  galleries,
  title,
  subtitle,
}) => {
  return (
    <Box
      sx={{
        pt: 0,
        pb: title || subtitle ? { xs: 6, md: 10 } : 0, // Only add bottom padding if title/subtitle is shown
        backgroundColor: "background.default",
      }}
    >
      <Container maxWidth="xl" sx={{ pt: 0 }}>
        {(title || subtitle) && (
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 4, md: 6 },
              pt: { xs: 2, md: 4 }, // Spacing from nav to title
              px: { xs: 2, sm: 3, md: 4 },
            }}
          >
            {title && (
              <Typography
                variant="h1"
                sx={{
                  fontFamily: '"Cinzel Decorative", serif',
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "4rem" },
                  fontWeight: 400,
                  mb: { xs: 1, md: 1.5 },
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body1"
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontStyle: "italic",
                  color: "text.secondary",
                  maxWidth: { xs: "100%", md: "800px" },
                  mx: "auto",
                  textAlign: "center",
                  lineHeight: { xs: 1.6, md: 1.7 },
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Grid Layout - Max 3 columns with even spacing */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // 1 column on mobile
              sm: "repeat(2, 1fr)", // 2 columns on small screens
              md: "repeat(3, 1fr)", // 3 columns on medium screens and up
            },
            gap: { xs: 3, sm: 4, md: 4 },
            width: "100%",
            justifyContent: "center", // Center the grid items
          }}
        >
          {galleries.map((gallery) => (
            <Box key={gallery.id}>
              <GalleryCard gallery={gallery} />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
